import styles from './contract-new-license-dialog.module.scss';
import { ValueFormatterParams } from 'ag-grid-community';
import { useState } from 'react';
import { useImmer } from 'use-immer';
import { GrayOutlinedButton, PrimaryButton } from '../../button/button';
import Dialog, { DialogActions, DialogContent, DialogTitle } from '../../dialog/dialog';
import { MenuItem } from '../../menu/menu';
import { NumberSelect } from '../../number-select/number-select';
import Select from '../../select/select';
import SimpleTable from '../../simple-table/simple-table';
import { CreateHeaders, currencyHeader, HeaderSizeEnum, textHeader, toCurrency } from '../../table-helpers/table-helpers';
import { useSingleContractDispatch } from '../contract-details-reducer/contract-details-reducer';
import { LicenseTypeEnum, ISellableProduct, ISellableProducts, CurrencyEnum } from '../../common-types/common-types';
import { CheckboxLabel } from '../../checkbox/checkbox';
import { t } from 'i18next';

/* eslint-disable-next-line */
export interface ContractNewLicenseDialogProps {
  onClose: () => void,
  sellableProducts: ISellableProducts[],
  currency: CurrencyEnum
}

export interface selectedItem extends ISellableProduct {
  quantity: number
}

export function ContractNewLicenseDialog(props: ContractNewLicenseDialogProps) {
  const dispatch = useSingleContractDispatch();
  const [sellableProducts, updateSellableProducts] = useImmer<ISellableProducts[]>(props.sellableProducts);
  const [showM2S, setShowM2S] = useState(false);
  const firstGroupWithSellableItems = props.sellableProducts.find(p => p.namedUsers.length > 0 || p.nlm.length > 0 || p.slm.length > 0 || p.oneTime.length > 0);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(firstGroupWithSellableItems?.groupName || "");
  const firstGroupHasNamedUser = !!firstGroupWithSellableItems && firstGroupWithSellableItems.namedUsers.length > 0;
  const [selectedType, setSelectedType] = useState<LicenseTypeEnum>(firstGroupHasNamedUser ? LicenseTypeEnum.NAMED_USER : LicenseTypeEnum.NLM);
  const [selectedItems, updateSelectedItems] = useImmer<any>({});

  const selectedGroup = sellableProducts.find(sp => sp.groupName === selectedGroupId);
  const typeOptions = [];

  if (selectedGroup && selectedGroup.namedUsers.length > 0) typeOptions.push({ id: LicenseTypeEnum.NAMED_USER, label: "Navngitt bruker" });
  if (selectedGroup && selectedGroup.nlm.length > 0) typeOptions.push({ id: LicenseTypeEnum.NLM, label: "Flerbruker" });
  if (selectedGroup && selectedGroup.slm.length > 0) typeOptions.push({ id: LicenseTypeEnum.SLM, label: "Enbruker" });
  if (selectedGroup && selectedGroup.oneTime.length > 0) typeOptions.push({ id: LicenseTypeEnum.ONE_TIME, label: "Engangsvarer" });
  const hasAnyItems = (sp: ISellableProducts) => sp.nlm.length > 0 || sp.slm.length > 0 || sp.oneTime.length > 0 || sp.namedUsers.length > 0;

  const groupOptions = props.sellableProducts
    .filter(sp => hasAnyItems(sp))
    .map(sp => { return { id: sp.groupName, label: sp.groupName } });

  const handleNewLicenses = () => {
    const items: Array<selectedItem> = Object.values(selectedItems);
    dispatch({ type: "NEW_LICENSES_CREATED", newLicenseItems: items });
    props.onClose();
  }

  const onGroupSelected = (event: any) => setSelectedGroupId(event.target.value);
  const onTypeSelected = (event: any) => setSelectedType(event.target.value);

  const getSelectedData = () => {
    let data: ISellableProduct[] = [];
    if (!selectedGroup) data = [];
    else if (selectedType === LicenseTypeEnum.NAMED_USER) data = selectedGroup?.namedUsers;
    else if (selectedType === LicenseTypeEnum.NLM) data = selectedGroup?.nlm;
    else if (selectedType === LicenseTypeEnum.SLM) data = selectedGroup?.slm;
    else if (selectedType === LicenseTypeEnum.ONE_TIME) data = selectedGroup?.oneTime;
    data = data?.filter((l: ISellableProduct) => l.vismaId.includes("M2S") === showM2S);

    return data;
  }
  const quantityRenderer = (params: ValueFormatterParams) => {
    const product: ISellableProduct = params.data;
    const vismaId = product.vismaId;
    const price = product.price;
    const name = product.productName;

    const handleChange = (value: number) => {
      updateSelectedItems((draft: any) => {
        if (selectedGroup) {
          if (product.connectedItem) {
            let connectedItemProduct = getSelectedData().find(i => i.vismaId === product.connectedItem?.idOfConnected);
            const currentConnectedItem = connectedItemProduct && draft[connectedItemProduct.vismaId];
            const isItemFull = currentConnectedItem && currentConnectedItem?.quantity === currentConnectedItem?.connectedItem.requiredQuantity;
            if (isItemFull) connectedItemProduct = getSelectedData().find(i => i.vismaId === currentConnectedItem?.connectedItem?.idOfConnected);

            if (!connectedItemProduct) throw new Error("Kunne ikke finne connectedItem");
            const currentItem: selectedItem = draft[connectedItemProduct.vismaId];

            if ((currentItem?.quantity > 0 || value > product.connectedItem.requiredQuantity)) {
              const currentQuantity = currentItem?.quantity || 0;
              const item: selectedItem = {
                quantity: (value + currentQuantity) - product.connectedItem.requiredQuantity,
                productName: connectedItemProduct.productName,
                licenseType: selectedType,
                vismaId: product.connectedItem.idOfConnected,
                price: connectedItemProduct?.price || 0,
                groupId: selectedGroup?.groupId,
                isModule: connectedItemProduct.isModule,
                connectedItem: connectedItemProduct.connectedItem,
                isConnectedItem: connectedItemProduct.isConnectedItem
              }
              draft[connectedItemProduct.vismaId] = item;
            }
            else {
              const item: selectedItem = {
                quantity: value,
                productName: name,
                licenseType: selectedType,
                vismaId: vismaId,
                price: price,
                groupId: selectedGroup?.groupId,
                isModule: product.isModule,
                connectedItem: product.connectedItem
              }
              draft[vismaId] = item
            }
          }
          else {
            const item: selectedItem = {
              quantity: value,
              productName: name,
              licenseType: selectedType,
              vismaId: vismaId,
              price: price,
              groupId: selectedGroup?.groupId,
              isModule: product.isModule,
              connectedItem: product.connectedItem
            }
            draft[vismaId] = item
          }
        }
      });
    }
    const existingItem: selectedItem = selectedItems[vismaId];

    return !existingItem?.isConnectedItem ? <NumberSelect
      value={existingItem?.quantity || 0}
      onValueChange={handleChange}
    /> : existingItem.quantity
  }
  const totalPriceRenderer = (params: ValueFormatterParams) => {
    const vismaId = params.data.vismaId;
    const existingItem = selectedItems[vismaId];
    const quantity = existingItem ? existingItem.quantity : 0;
    return toCurrency(params.data.price * quantity, props.currency);
  }
  return (
    <Dialog
      open={true}
      maxWidth="xl"
      fullWidth>
      <DialogTitle className='mb-4'>
        {t("standard.newLicense")}
      </DialogTitle>
      <DialogContent>
        <div className="d-flex flex-column mb-3">
          <div className='mb-3'>
            <Select
              fullWidth
              value={selectedGroupId}
              label={t("standard.product")}
              sx={{ width: 300 }}
              onChange={onGroupSelected}>
              {groupOptions.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
            </Select>
          </div>
          <div className='mb-3'>
            <Select
              fullWidth
              value={selectedType}
              label={t("standard.licenseType")}
              sx={{ width: 300 }}
              onChange={onTypeSelected}>
              {typeOptions.map(o => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
            </Select>
          </div>
          <div>
            <CheckboxLabel
              checked={showM2S}
              label={t("standard.showM2S")}
              onChange={() => setShowM2S(!showM2S)}
            />
          </div>
        </div>
        <SimpleTable
          headers={CreateHeaders([
            textHeader("productName", t("standard.name"), HeaderSizeEnum.EXTRA_LARGE),
            textHeader("quantity", t("standard.quantity"), HeaderSizeEnum.MEDIUM, { renderer: quantityRenderer }),
            currencyHeader("price", t("standard.price"), HeaderSizeEnum.LARGE, {
              cellStyle: { justifyContent: 'right' },
              headerClass: "ag-right-aligned-header"
            }),
            currencyHeader("", t("standard.totalPrice"), HeaderSizeEnum.LARGE, {
              renderer: totalPriceRenderer,
              cellStyle: { justifyContent: 'right' },
              headerClass: "ag-right-aligned-header"
            })
          ])}
          data={getSelectedData()}
          disableSearch={true}
          isCompactTable={true}
          resizable={true}
          disablePagination={true}
        />
      </DialogContent>
      <DialogActions>
        <PrimaryButton
          variant='contained'
          onClick={handleNewLicenses}
          size='large'
          className='minButtonWidth'>
          OK
        </PrimaryButton>
        <GrayOutlinedButton
          onClick={props.onClose}
          size='large'
          className='minButtonWidth'>
          {t("standard.cancel")}
        </GrayOutlinedButton>
      </DialogActions>
    </Dialog>
  );
}

export default ContractNewLicenseDialog;